import { Callout, Panes } from '@wtfalch/design';
import { useEffect, useRef, useState } from 'react';

import { useCallSession } from '../calls/CallSession.tsx';
import { CallSurface } from '../calls/CallSurface.tsx';
import { eventCallRoomId } from '../calls/eventCalls.ts';
import type { RoomCallBinding } from '../calls/roomCalls.ts';

/**
 * What `/h/<agoraId>/calendar/call/<uid>` opens: the call for that event's
 * room, joined as soon as the page is there. The calls service admits any
 * signed-in member into an `event:` room. The call itself lives in the tab's
 * call session, so opening this page again while on the call shows it, and
 * never joins a second time.
 */
export function EventCall({
  calls,
  uid,
  onLeft,
}: {
  calls: RoomCallBinding;
  uid: string;
  onLeft: () => void;
}) {
  const session = useCallSession();
  const roomId = eventCallRoomId(uid);
  const onThisCall = session?.call?.roomId === roomId;
  const onOtherCall = Boolean(session?.call) && !onThisCall;
  const [error, setError] = useState<string | null>(null);
  const sessionRef = useRef(session);
  sessionRef.current = session;
  const onLeftRef = useRef(onLeft);
  onLeftRef.current = onLeft;

  // Only the first `calls` for this uid joins; a fresh object per render must not rejoin.
  // biome-ignore lint/correctness/useExhaustiveDependencies: `calls` is read once per uid.
  useEffect(() => {
    const current = sessionRef.current;
    setError(null);
    if (!current || current.call) {
      if (current?.call?.roomId === roomId) current.setFocused(true);
      return;
    }
    let live = true;
    current
      .start({ roomId, title: 'Event call', path: window.location.pathname }, calls)
      .then((result) => {
        if (!live) return;
        if (result.ok) sessionRef.current?.setFocused(true);
        else setError(result.error);
      });
    return () => {
      live = false;
    };
  }, [uid]);

  // Leaving the call, or losing it, closes this page.
  const wasOnThisCall = useRef(false);
  useEffect(() => {
    if (wasOnThisCall.current && !onThisCall) onLeftRef.current();
    wasOnThisCall.current = onThisCall;
  }, [onThisCall]);

  return (
    <Panes.Column flex>
      {error && <Callout tone="bad">{error}</Callout>}
      {onOtherCall && <Callout tone="bad">Leave the call you are on first.</Callout>}
      <CallSurface roomId={roomId} className="room-call-grid flex-1 min-h-0" />
    </Panes.Column>
  );
}
